import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import UnifiedHoldRequestForm from '@/components/forms/UnifiedHoldRequestForm';
import { post } from '@/lib/api-client';

interface ValidationError {
  rowNumber: number;
  field: string;
  message: string;
}

interface ProcessedRow {
  rowNumber: number;
  holdRequestId?: string;
  lineItemId?: string;
  success: boolean;
  errors: ValidationError[];
}

interface UploadResult {
  totalRows: number;
  successfulRows: number;
  failedRows: number;
  holdRequestsCreated: number;
  lineItemsCreated: number;
  errors: ValidationError[];
  processedRows: ProcessedRow[];
}

export default function CreateHoldRequestUnified() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Preserve form state for error recovery
  const [savedFormData, setSavedFormData] = useState<{ holdRequest: { advertiser: string; businessCategory: string; isCrossRegion: boolean; serviceAeId?: string; holdExpirationDate?: string }; lineItems: Record<string, string | number | boolean | undefined>[] } | null>(null);

  // Ref for scrolling to results after successful submission
  const resultsRef = useRef<HTMLDivElement>(null);

  // Scroll to results section when submission succeeds
  useEffect(() => {
    if (uploadResult && uploadResult.failedRows === 0 && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [uploadResult]);

  const handleSubmit = async (data: { holdRequest: { advertiser: string; businessCategory: string; isCrossRegion: boolean; serviceAeId?: string; holdExpirationDate?: string }; lineItems: Record<string, string | number | boolean | undefined>[] }) => {
    setIsProcessing(true);
    setError(null);
    setUploadResult(null);
    
    // Save form data in case of errors
    setSavedFormData(data);

    try {
      const result = await post<{
        success: boolean;
        message: string;
        data: UploadResult;
      }>('/holds/process-bulk-data', {
        data: data.lineItems,
        holdRequestDetails: data.holdRequest,
      });

      setUploadResult(result.data);
      // If there were failures, the uploadResult is set and the form's error panel handles it.
      // Throw to prevent the form from clearing its data.
      if (result.data?.failedRows > 0) {
        throw { __validationFailure: true };
      }
    } catch (err: unknown) {
      console.error('Processing error:', err);
      
      // Validation failures (200 with failedRows > 0) — uploadResult already set, no generic banner
      const errObj = err as Record<string, unknown> | undefined;
      if (errObj?.__validationFailure) {
        throw err; // re-throw to prevent form clearing
      }

      // API errors (4xx/5xx) — extract structured data from axios interceptor
      const response = errObj?.response as Record<string, unknown> | undefined;
      const responseData = response?.data as Record<string, unknown> | undefined;
      const errorData = (errObj?.details ?? responseData?.data) as UploadResult | undefined;
      if (errorData && errorData.totalRows !== undefined) {
        setUploadResult(errorData);
      } else {
        // Truly unexpected error — show generic banner
        const errorMessage = err instanceof Error ? err.message : 'Failed to process data';
        setError(errorMessage);
      }
      
      // Re-throw to prevent form from clearing
      throw err;
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setUploadResult(null);
    setError(null);
    setSavedFormData(null);
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="text-primary-600 hover:text-primary-700 flex items-center text-sm font-medium"
        >
          <svg
            className="w-4 h-4 mr-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>
      </div>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-neutral-900">Create New Hold Request</h1>
        <p className="mt-2 text-neutral-600">
          Submit a new inventory hold request with contract-level details and line items below.
        </p>
      </div>

      {/* Unified Form - always visible */}
      <UnifiedHoldRequestForm 
        onSubmit={handleSubmit}
        isProcessing={isProcessing}
        onCancel={() => {
          localStorage.removeItem('holdRequestFormDraft');
          navigate('/');
          window.scrollTo(0, 0);
        }}
        initialData={savedFormData}
        uploadErrors={uploadResult && uploadResult.failedRows > 0 ? uploadResult : null}
        onDismissErrors={() => setUploadResult(null)}
      />

      {/* Success Results Section - shown below the form when all rows succeeded */}
      {uploadResult && uploadResult.failedRows === 0 && (
        <div ref={resultsRef} className="mt-6 p-6 bg-white border border-neutral-200 rounded-lg ">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4">Processing Results</h3>
          
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-neutral-50 rounded-md">
              <p className="text-sm text-neutral-600">Total Rows</p>
              <p className="text-2xl font-bold text-neutral-900">{uploadResult.totalRows}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-md">
              <p className="text-sm text-green-600">Successful</p>
              <p className="text-2xl font-bold text-green-900">{uploadResult.successfulRows}</p>
            </div>
            <div className="p-4 bg-red-50 rounded-md">
              <p className="text-sm text-red-600">Failed</p>
              <p className="text-2xl font-bold text-red-900">{uploadResult.failedRows}</p>
            </div>
            <div className="p-4 bg-outfront-purple-light rounded-md">
              <p className="text-sm text-outfront-purple">Requests Created</p>
              <p className="text-2xl font-bold text-outfront-purple">{uploadResult.holdRequestsCreated}</p>
            </div>
          </div>

          {/* Success Message */}
          <div className="p-4 bg-green-50 border border-green-200 rounded-md mb-4">
            <p className="text-sm text-green-800">
              <strong>Success!</strong> All rows were processed successfully.
              Created {uploadResult.holdRequestsCreated} hold request(s) with{' '}
              {uploadResult.lineItemsCreated} line item(s).
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-neutral-200">
            <button
              onClick={handleReset}
              className="px-4 py-2 text-sm font-medium text-neutral-600 bg-white border border-neutral-200 rounded-md hover:bg-neutral-50"
            >
              Create Another Request
            </button>
            {uploadResult.processedRows.length > 0 && uploadResult.processedRows[0]?.holdRequestId && (
              <button
                onClick={() => {
                  const holdRequestId = uploadResult.processedRows[0]?.holdRequestId;
                  if (holdRequestId) {
                    queryClient.invalidateQueries({ queryKey: ['holdRequests'] });
                    queryClient.invalidateQueries({ queryKey: ['holdRequest', holdRequestId] });
                    navigate(`/holds/${holdRequestId}`);
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
              >
                View Hold Request
              </button>
            )}
            <button
              onClick={() => {
                queryClient.invalidateQueries({ queryKey: ['holdRequests'] });
                queryClient.invalidateQueries({ queryKey: ['recentActivity'] });
                navigate('/');
              }}
              className="px-4 py-2 text-sm font-medium text-neutral-600 bg-white border border-neutral-200 rounded-md hover:bg-neutral-50"
            >
              View All Requests
            </button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">
            <strong>Error:</strong> {error}
          </p>
        </div>
      )}
    </div>
  );
}
